import { useTranslation } from "react-i18next";
import { TableSectionWrapper } from "./styles";
import CustomTable from "@Common/CustomTable";
import { Dispatch, MouseEvent, SetStateAction, useMemo, useState } from "react";
import LabelVariants from "./labelVariants";
import { Button, ListSubheader, Menu, MenuItem } from "@mui/material";
import { PiDotsThreeOutlineFill } from "react-icons/pi";
import EditUserManagementCentre from "../editUserManagementCentre";
import DeleteUserManagementRecord from "../deleteUserManagementRecord";
import { IResponseGetAllOwnerUsers } from "src/services/userManagement/GetAllOwnerUsers/types";
import { CustomNoRowsOverlay } from "./CustomNoRowsOverlay";
import { ReservationFiltersState } from "../../../../../src/components/ReservationManagement/ReservationFilters";
interface IProps {
  totalRecords: number;
  totalPages: number;
  activePage: number;
  perPage: string;
  setActivePage: Dispatch<SetStateAction<number>>;
  onPerPageChange: Dispatch<SetStateAction<string>>;
  dataList: IResponseGetAllOwnerUsers | undefined;
  filters: ReservationFiltersState;
  onFiltersChange: (filters: ReservationFiltersState) => void;
  onClearFilters: () => void;
  onNameSearchChange?: (searchTerm: string) => void; // Add name search callback
  loading: boolean;
}
export default function TableSection({
  activePage,
  perPage,
  setActivePage,
  onPerPageChange,
  dataList,
  totalRecords,
  totalPages,
  filters,
  onFiltersChange,
  onClearFilters,
  onNameSearchChange,
  loading,
}: IProps) {
  /* ---------------------------------- Hooks --------------------------------- */
  const {
    t,
    i18n: { language },
  } = useTranslation();
  /* ------------------------------- Local State ------------------------------ */
  const [selectedParams, setSelectedParams] = useState<any>(null); // Store selected row data
  const [selectedRecord, setSelectedRecord] = useState<{
    userId: number;
    userTypeId: number;
  }>();
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [isUserManagementCentreOpen, setIsUserManagementCentreOpen] =
    useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleClick = (event: MouseEvent<HTMLButtonElement>, params: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedParams(params);
  };

  const columns = [
    {
      field: "fullName",
      headerName: t("UserManagement.name"),
      flex: 1,
      renderCell: (params: any) => (
        <p
          className="columnsContent"
          style={{
            textAlign: `${language === "ar" ? "right" : "left"}`,
            margin: 0,
          }}
        >
          {params.row.fullName}
        </p>
      ),
    },
    {
      field: "userTypeId",
      headerName: t("UserManagement.user_type"),
      flex: 1,
      renderCell: (params: any) => (
        <div
          className="columnsContent"
          style={{
            textAlign: `${language === "ar" ? "right" : "left"}`,
            margin: 0,
            height: "100%",
          }}
        >
          <LabelVariants
            label={params.row.userTypeId.label}
            status={params.row.userTypeId.value}
          />
        </div>
      ),
    },
    {
      field: "phoneNumber",
      headerName: t("UserManagement.phoneNumber"),
      flex: 1,
      renderCell: (params: any) => (
        <p
          className="columnsContent"
          style={{
            textAlign: `${language === "ar" ? "right" : "left"}`,
            margin: 0,
            direction: "ltr",
          }}
        >
          {params.row.phoneNumber}
        </p>
      ),
    },
    {
      field: "status",
      headerName: t("UserManagement.status"),
      flex: 1,
      renderCell: (params: any) => (
        <div
          className="columnsContent"
          style={{
            textAlign: `${language === "ar" ? "right" : "left"}`,
            margin: 0,
            height: "100%",
          }}
        >
          <LabelVariants
            label={params.row.status.label}
            status={params.row.status.value}
            withbadge
          />
        </div>
      ),
    },
    {
      field: "action",
      headerName: "",
      flex: 1,
      renderCell: (params: any) => (
        <div>
          <Button
            id="basic-button"
            aria-controls={open ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={(event) => handleClick(event, params)}
          >
            <PiDotsThreeOutlineFill
              style={{ fontSize: "25px", color: "#625783" }}
            />
          </Button>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <ListSubheader>{t("UserManagement.team_settings")}</ListSubheader>

            <MenuItem
              disabled={selectedParams?.row?.userTypeId_ === 5}
              onClick={() => {
                setAnchorEl(null);
                setIsUserManagementCentreOpen(true);
                setSelectedRecord({
                  userId: selectedParams?.row?.id,
                  userTypeId: selectedParams?.row?.userTypeId_,
                });
              }}
            >
              {t("UserManagement.edit")}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setIsDeletePopupOpen(true);
                setSelectedRecord({
                  userId: selectedParams?.row?.id,
                  userTypeId: selectedParams?.row?.userTypeId_,
                });
              }}
            >
              {t("UserManagement.delete")}
            </MenuItem>
          </Menu>
        </div>
      ),
    },
  ];

  const ListOfUsers = useMemo(() => {
    const data = dataList?.result.usersList.map((item) => {
      return {
        id: item.id,
        fullName: item.fullName,
        userTypeId_: item.userTypeId,
        userTypeId:
          item.userTypeId === 5
            ? { label: t("UserManagement.user_owner"), value: "orange" }
            : item.userTypeId === 2
              ? { label: t("UserManagement.team_member"), value: "blue" }
              : item.userTypeId === 3
                ? {
                    label: t("UserManagement.service_providers"),
                    value: "purple",
                  }
                : "",
        phoneNumber: item.phoneNumber,
        status:
          item.teamMemberStatusId === 0
            ? { label: t("UserManagement.pending"), value: "yellow" }
            : item.teamMemberStatusId === 1
              ? { label: t("UserManagement.active"), value: "green" }
              : "",
      };
    });
    return data ?? [];
  }, [dataList, t]);

  // Convert filter state to show translated values for UI
  const displayFilters = {
    ...filters,
    enum: filters.enum.map((key) => {
      const mapping: Record<string, string> = {
        UserOwner: t("UserManagement.user_owner"),
        TeamMember: t("UserManagement.team_member"),
        ServiceProvider: t("UserManagement.service_providers"),
      };
      return mapping[key] || key;
    }),
  };

  return (
    <TableSectionWrapper>
      {isDeletePopupOpen && (
        <DeleteUserManagementRecord
          setIsDeletePopupOpen={setIsDeletePopupOpen}
          selectedRecord={selectedRecord}
        />
      )}
      {isUserManagementCentreOpen && (
        <EditUserManagementCentre
          onclose={() => setIsUserManagementCentreOpen(false)}
          selectedRecord={selectedRecord}
        />
      )}
      <div className="Header">
        <p>{t("UserManagement.users_list")}</p>
      </div>
      <CustomTable
        filters={displayFilters}
        onFiltersChange={onFiltersChange}
        onClearFilters={onClearFilters}
        columns={columns}
        rows={ListOfUsers}
        totalPages={totalPages}
        totalRecords={totalRecords}
        setActivePage={setActivePage}
        activePage={activePage}
        onPerpageChange={onPerPageChange}
        perPage={perPage}
        listPerPagesNumber={[
          { label: "20", value: "20" },
          { label: "50", value: "50" },
          { label: "100", value: "100" },
        ]}
        CustomNoRowsOverlay={CustomNoRowsOverlay}
        visibleFilters={["name", "enum"]}
        enumOptions={[
          t("UserManagement.user_owner"),
          t("UserManagement.team_member"),
          t("UserManagement.service_providers"),
        ]}
        onNameSearchChange={onNameSearchChange}
        loading={loading}
        nameFilterPlaceholder={t("UserManagement.name")}
        EnumFilterLabel={t("UserManagement.user_type")}
      />
    </TableSectionWrapper>
  );
}
